import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withKeys as _withKeys, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = {
  key: 0,
  class: "f-input__required"
}
const _hoisted_3 = ["type", "name", "value", "disabled"]

import { ref, computed, watch, inject, onMounted, onUnmounted } from 'vue';
import utils from '@/utils';
import { FormInputEmits, FormInputProps, FormInputRef } from '@/types/form/form-input';
import { DynamicFormContext, DynamicFormFieldValidateResult } from '@/types/form/dynamic-form';
import FormElMsg from '@/form/other/FormElMsg.vue';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import services from '@/services';
import { uniqBy } from 'lodash-es';


export default /*@__PURE__*/_defineComponent({
  __name: 'FormInput',
  props: {
    item: {},
    showSearchIcon: { type: Boolean }
  },
  emits: ["change", "icon-click"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;
const formContext = inject<DynamicFormContext>('formContext')!;
const inputRef = ref<HTMLInputElement>();
const value = ref('');
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const focused = ref(false);
const msg = ref<FormElMsgData>();

const fields = computed(() => props.item?.fields);
const inputType = computed(() => utils.sp.getVal(fields.value?.inputType));
const hasValue = computed(() => !utils.obj.isNoE(value.value));
const label = computed(() => fields.value?.label?.value || '');
const placeholder = computed(() => fields.value?.placeholder?.value || '');
const fieldName = computed(() => fields.value?.fieldName?.value || '');
const showSearchIcon = computed(() => fields.value?.showSearchIcon?.value || false);

const onMainClick = () => {
  inputRef.value!.focus();
};
const onFocus = () => {
  focused.value = true;
};
const onBlur = () => {
  focused.value = false;
};
const onInput = (e: InputEvent) => {
  const el = e.target as HTMLInputElement | undefined;
  value.value = el?.value ?? '';
  msg.value = undefined;
  emit('change', 'form-input', fieldName.value, value.value, value.value);
};
const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue?: string | null) => {
  value.value = newValue || '';
};

const getValue = () => {
  return value.value;
};

const onIconClick = () => {
  emit('icon-click', fieldName.value, value.value);
};

const onKeyupEnter = () => {
  if (showSearchIcon.value) {
    onIconClick();
  }
};

const validate = async () => {
  const result: DynamicFormFieldValidateResult = {
    value: value.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value || !fields.value) {
    return result;
  }
  if (isRequired.value && utils.obj.isNoE(value.value)) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  const validators = [];
  if (inputType.value === 'tel') {
    validators.push(await services.form.getPatternValidator('Phone Validator'));
  }
  if (inputType.value === 'email') {
    validators.push(await services.form.getPatternValidator('Email Validator'));
  }
  validators.push(...fields.value.validators);
  if (fields.value.privateValidators && fields.value.privateValidators.length > 0) {
    validators.push(...fields.value.privateValidators);
  }
  uniqBy(validators, (val) => val.id);
  const validatorsError = utils.form.validateField(value.value, validators);
  if (validatorsError.length > 0) {
    result.valid = false;
    result.message = validatorsError[0];
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  msg.value = undefined;
  return result;
};

const getRefData = (): FormInputRef => ({
  el: inputRef.value!,
  type: 'form-input',
  name: fieldName.value,
  id: props.item.id,
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  getValue,
  validate,
});

// Register this component with parent
watch(
  () => props.item,
  () => {
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
});
onUnmounted(() => {
  formContext.unRegFormEl(getRefData());
});
__expose(getRefData());

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (fields.value && isVisible.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(['f-input', { disabled: isDisabled.value, 'has-error': msg.value?.type === 'error' }])
      }, [
        _createElementVNode("div", {
          class: "f-input__main",
          onClick: onMainClick
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["f-input__placeholder", [{ 'as-label': focused.value || hasValue.value }]])
          }, [
            _createElementVNode("span", {
              class: "f-input__label",
              innerHTML: focused.value || hasValue.value ? label.value : _unref(utils).string.ifEmpty(placeholder.value, label.value)
            }, null, 8, _hoisted_1),
            (isRequired.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
              : _createCommentVNode("", true)
          ], 2),
          _createElementVNode("input", {
            type: inputType.value || 'text',
            name: `${fieldName.value}_${(Math.random() * 1000).toFixed(0)}`,
            value: value.value,
            disabled: isDisabled.value,
            onFocus: onFocus,
            onBlur: onBlur,
            onInput: onInput,
            onKeyup: _withKeys(onKeyupEnter, ["enter"]),
            autocomplete: "new-password",
            ref_key: "inputRef",
            ref: inputRef
          }, null, 40, _hoisted_3),
          (showSearchIcon.value)
            ? (_openBlock(), _createBlock(_component_Icon, {
                key: 0,
                name: "search",
                size: "xs",
                class: "f-input__search-icon",
                onClick: _withModifiers(onIconClick, ["stop"])
              }))
            : _createCommentVNode("", true)
        ]),
        _createVNode(FormElMsg, { msg: msg.value }, null, 8, ["msg"])
      ], 2))
    : _createCommentVNode("", true)
}
}

})